// ======================================================================
// FILE:
// src/modules/finance/transactions/receivables/pages/ReceivablesListPage.js
//
// MODULE: Finance - Accounts Receivable
// PURPOSE: Outstanding Trading Sales Invoice workbench
// ======================================================================

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import AsyncSelect from "react-select/async";
import { Modal, Spinner } from "react-bootstrap";

import {
    listReceivables,
    getReceivable,
    validateReceipt,
    createReceiptForInvoice,
} from "../services/receivablesService";

import financeLookupService from "../../../services/financeLookupService";

// ======================================================================
// HELPERS
// ======================================================================

const today = () =>
    new Date().toISOString().slice(0, 10);

const amount = (value, currency = "INR") => {
    const code =
        /^[A-Z]{3}$/.test(String(currency || ""))
            ? currency
            : "INR";

    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));
};

const dateText = (value) => {
    if (!value) return "—";

    const raw = String(value).slice(0, 10);
    const parts = raw.split("-");

    if (parts.length !== 3) return raw;

    return new Date(`${raw}T00:00:00`).toLocaleDateString(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
};

const pick = (row, ...keys) => {
    for (const key of keys) {
        if (
            row?.[key] !== undefined &&
            row?.[key] !== null
        ) {
            return row[key];
        }
    }

    return null;
};

// ======================================================================
// MAIN PAGE
// ======================================================================

export default function ReceivablesListPage() {

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [search, setSearch] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [paymentStatus, setPaymentStatus] = useState("ALL");

    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);

    const [showDetail, setShowDetail] = useState(false);
    const [showReceipt, setShowReceipt] = useState(false);

    const [detailLoading, setDetailLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Receipt form

    const [receiptDate, setReceiptDate] = useState(today());
    const [receiptAmount, setReceiptAmount] = useState("");
    const [receiptMode, setReceiptMode] = useState("BANK");
    const [receiptAccount, setReceiptAccount] = useState(null);

    const [referenceNo, setReferenceNo] = useState("");
    const [referenceDate, setReferenceDate] = useState("");
    const [remarks, setRemarks] = useState("");

    const [formError, setFormError] = useState("");

    // ==================================================================
    // LOAD RECEIVABLES
    // ==================================================================

    const loadRows = useCallback(async () => {

        try {
            setLoading(true);
            setLoadError("");

            const result = await listReceivables({
                search: search.trim() || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                payment_status: paymentStatus,
            });

            setRows(
                Array.isArray(result) ? result : []
            );

        } catch (error) {

            setRows([]);

            setLoadError(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to load receivables."
            );

        } finally {
            setLoading(false);
        }

    }, [search, dateFrom, dateTo, paymentStatus]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    // ==================================================================
    // NORMALIZE API RESPONSE
    // ==================================================================

    const normalizedRows = useMemo(() => {

        return rows.map((row) => ({
            ...row,

            id: pick(
                row,
                "invoice_id",
                "id",
                "sales_invoice_id"
            ),

            invoice_no: pick(
                row,
                "invoice_no",
                "invoice_number",
                "si_no"
            ),

            invoice_date: pick(
                row,
                "invoice_date",
                "si_date"
            ),

            customer_name:
                pick(
                    row,
                    "customer_name",
                    "customer",
                    "party_name"
                ) || "—",

            currency:
                pick(
                    row,
                    "currency_code",
                    "currency"
                ) || "INR",

            invoice_amount:
                pick(
                    row,
                    "invoice_amount",
                    "net_amount",
                    "original_amount"
                ) || 0,

            received_amount:
                pick(
                    row,
                    "received_amount",
                    "allocated_amount",
                    "paid_amount"
                ) || 0,

            outstanding_amount:
                pick(
                    row,
                    "outstanding_amount",
                    "balance_amount"
                ) || 0,

            payment_status:
                pick(row, "payment_status") || (
                    Number(pick(row, "outstanding_amount", "balance_amount") || 0) <= 0
                        ? "PAID"
                        : Number(pick(row, "received_amount", "allocated_amount", "paid_amount") || 0) <= 0
                            ? "UNPAID"
                            : "PARTIALLY_PAID"
                ),

            company_id: pick(row, "company_id"),

        }));

    }, [rows]);

    // ==================================================================
    // SUMMARY TOTALS
    // ==================================================================

    const totals = useMemo(() => {

        return normalizedRows.reduce(
            (acc, row) => {

                acc.invoice += Number(
                    row.invoice_amount || 0
                );

                acc.received += Number(
                    row.received_amount || 0
                );

                acc.outstanding += Number(
                    row.outstanding_amount || 0
                );

                return acc;

            },
            {
                invoice: 0,
                received: 0,
                outstanding: 0,
            }
        );

    }, [normalizedRows]);

    // ==================================================================
    // VIEW INVOICE DETAILS
    // ==================================================================

    const openDetail = async (row) => {

        setSelected(row);
        setShowDetail(true);
        setDetail(null);
        setDetailLoading(true);
        setFormError("");

        try {

            const result = await getReceivable(row.id);

            setDetail(result);

        } catch (error) {

            setFormError(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to load invoice details."
            );

        } finally {
            setDetailLoading(false);
        }
    };

    // ==================================================================
    // OPEN RECEIVE PAYMENT
    // ==================================================================

    const openReceipt = (row) => {

        setSelected(row);

        setReceiptDate(today());

        setReceiptAmount(
            String(row.outstanding_amount || "")
        );

        setReceiptMode("BANK");
        setReceiptAccount(null);

        setReferenceNo("");
        setReferenceDate("");
        setRemarks("");

        setFormError("");
        setShowReceipt(true);
    };

    // ==================================================================
    // LOAD BANK / CASH ACCOUNTS
    // ==================================================================

    const loadPaymentAccounts = async (inputValue) => {

        if (!selected?.company_id) {
            return [];
        }

        try {

            const accounts =
                await financeLookupService.searchPaymentAccounts(
                    selected.company_id,
                    inputValue || "",
                    20
                );

            return (accounts || []).map((account) => ({
                ...account,

                value: account.id,

                label:
                    `${account.account_name || "Unnamed Account"}` +
                    `${account.account_number
                        ? ` - ${account.account_number}`
                        : ""}` +
                    `${account.branch_name
                        ? ` (${account.branch_name})`
                        : ""}`,
            }));

        } catch (error) {

            console.error(
                "Receipt account lookup failed:",
                error
            );

            return [];
        }
    };

    // ==================================================================
    // CREATE DRAFT RECEIPT
    // ==================================================================

    const submitReceipt = async (event) => {

        event.preventDefault();
        setFormError("");

        if (!selected?.id) return;

        if (
            !receiptDate ||
            !receiptAmount ||
            Number(receiptAmount) <= 0 ||
            !receiptAccount?.value ||
            !receiptMode
        ) {

            setFormError(
                "Enter receipt date, a valid amount, receipt mode and receipt account."
            );

            return;
        }
                try {

            setSaving(true);

            // Validate amount against current outstanding

            await validateReceipt(
                selected.id,
                {
                    receipt_amount: receiptAmount,
                }
            );

            // Create DRAFT Receipt Voucher

            await createReceiptForInvoice(
                selected.id,
                {
                    voucher_date: receiptDate,

                    receipt_account_id:
                        receiptAccount.value,

                    receipt_mode: receiptMode,

                    receipt_amount: receiptAmount,

                    reference_no:
                        referenceNo || null,

                    reference_date:
                        referenceDate || null,

                    remarks:
                        remarks || null,
                }
            );

            setShowReceipt(false);

            await loadRows();

            window.alert(
                "Draft Receipt Voucher created successfully. It is available in the existing Finance Receipt Voucher workflow."
            );

        } catch (error) {

            setFormError(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to create draft receipt."
            );

        } finally {
            setSaving(false);
        }
    };

    // ==================================================================
    // DETAIL RESPONSE
    // ==================================================================

    const detailData =
        detail?.data || detail || {};

    const outstandingInfo =
        detailData?.outstanding || detailData;

    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <div className="container-fluid py-3">

            {/* PAGE HEADER */}

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">

                <div>
                    <h4 className="mb-1">
                        Accounts Receivable
                    </h4>

                    <div className="text-muted small">
                        Outstanding Trading Sales Invoices
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={loadRows}
                    disabled={loading}
                >
                    <i className="bi bi-arrow-clockwise me-1" />
                    Refresh
                </button>

            </div>

            {/* SUMMARY CARDS */}

            <div className="row g-3 mb-3">

                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="text-muted small">
                                Invoice Amount
                            </div>
                            <div className="fs-4 fw-semibold">
                                {amount(totals.invoice)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="text-muted small">
                                Received Amount
                            </div>
                            <div className="fs-4 fw-semibold text-success">
                                {amount(totals.received)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-4">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <div className="text-muted small">
                                Total Outstanding
                            </div>
                            <div className="fs-4 fw-semibold text-danger">
                                {amount(totals.outstanding)}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* MAIN TABLE */}

            <div className="card border-0 shadow-sm">

                <div className="card-body">

                    {/* FILTERS */}

                    <div className="row g-2 align-items-end mb-3">

                        <div className="col-12 col-lg-4">
                            <label className="form-label small fw-semibold">
                                Search
                            </label>

                            <input
                                className="form-control form-control-sm"
                                value={search}
                                onChange={(e) =>
                                    setSearch(e.target.value)
                                }
                                placeholder="Invoice number or customer"
                            />
                        </div>

                        <div className="col-6 col-lg-2">
                            <label className="form-label small fw-semibold">
                                Invoice From
                            </label>

                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={dateFrom}
                                onChange={(e) =>
                                    setDateFrom(e.target.value)
                                }
                            />
                        </div>

                        <div className="col-6 col-lg-2">
                            <label className="form-label small fw-semibold">
                                Invoice To
                            </label>

                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={dateTo}
                                onChange={(e) =>
                                    setDateTo(e.target.value)
                                }
                            />
                        </div>

                        <div className="col-12 col-lg-2">
                            <label className="form-label small fw-semibold">
                                Payment Status
                            </label>

                            <select
                                className="form-select form-select-sm"
                                value={paymentStatus}
                                onChange={(e) => setPaymentStatus(e.target.value)}
                            >
                                <option value="ALL">All</option>
                                <option value="UNPAID">Unpaid</option>
                                <option value="PARTIALLY_PAID">Partially Paid</option>
                                <option value="PAID">Paid</option>
                            </select>
                        </div>

                        <div className="col-12 col-lg-2 d-flex gap-2">

                            <button
                                className="btn btn-primary btn-sm"
                                onClick={loadRows}
                                disabled={loading}
                            >
                                Apply Filters
                            </button>

                            <button
                                className="btn btn-light border btn-sm"
                                onClick={() => {
                                    setSearch("");
                                    setDateFrom("");
                                    setDateTo("");
                                    setPaymentStatus("ALL");
                                }}
                            >
                                Clear
                            </button>

                        </div>

                    </div>

                    {loadError && (
                        <div className="alert alert-danger py-2">
                            {loadError}
                        </div>
                    )}

                    {/* TABLE TITLE */}

                    <div className="d-flex justify-content-between align-items-center mb-2">

                        <h6 className="mb-0">
                            Sales Invoices
                        </h6>

                        <span className="text-muted small">
                            {normalizedRows.length} invoice(s)
                        </span>

                    </div>

                    {/* INVOICE TABLE */}

                    <div className="table-responsive">

                        <table className="table table-hover table-sm align-middle mb-0">

                            <thead className="table-light">
                                <tr>
                                    <th>Invoice No.</th>
                                    <th>Invoice Date</th>
                                    <th>Customer</th>
                                    <th className="text-end">
                                        Invoice Amount
                                    </th>
                                    <th className="text-end">
                                        Received
                                    </th>
                                    <th className="text-end">
                                        Outstanding
                                    </th>
                                    <th className="text-center">Payment Status</th>
                                    <th className="text-center">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>

                                {loading ? (

                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="text-center py-4"
                                        >
                                            <Spinner
                                                size="sm"
                                                className="me-2"
                                            />
                                            Loading receivables...
                                        </td>
                                    </tr>

                                ) : normalizedRows.length === 0 ? (

                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="text-center text-muted py-4"
                                        >
                                            No invoices found.
                                        </td>
                                    </tr>

                                ) : (

                                    normalizedRows.map((row) => (

                                        <tr key={row.id}>

                                            <td className="fw-semibold">
                                                {row.invoice_no ||
                                                    `Invoice #${row.id}`}
                                            </td>

                                            <td>
                                                {dateText(row.invoice_date)}
                                            </td>

                                            <td>
                                                {row.customer_name}
                                            </td>

                                            <td className="text-end">
                                                {amount(
                                                    row.invoice_amount,
                                                    row.currency
                                                )}
                                            </td>

                                            <td className="text-end">
                                                {amount(
                                                    row.received_amount,
                                                    row.currency
                                                )}
                                            </td>

                                            <td className="text-end fw-semibold text-danger">
                                                {amount(
                                                    row.outstanding_amount,
                                                    row.currency
                                                )}
                                            </td>

                                            <td className="text-center">
                                                <span
                                                    className={`badge ${
                                                        row.payment_status === "PAID"
                                                            ? "bg-success"
                                                            : row.payment_status === "PARTIALLY_PAID"
                                                                ? "bg-warning text-dark"
                                                                : "bg-secondary"
                                                    }`}
                                                >
                                                    {row.payment_status === "PARTIALLY_PAID"
                                                        ? "Partially Paid"
                                                        : row.payment_status === "UNPAID"
                                                            ? "Unpaid"
                                                            : "Paid"}
                                                </span>
                                            </td>

                                            <td className="text-center text-nowrap">

                                                <button
                                                    className="btn btn-outline-secondary btn-sm me-1"
                                                    onClick={() =>
                                                        openDetail(row)
                                                    }
                                                >
                                                    View
                                                </button>

                                                {row.payment_status !== "PAID" && Number(row.outstanding_amount || 0) > 0 && (
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        onClick={() =>
                                                            openReceipt(row)
                                                        }
                                                    >
                                                        Receive Payment
                                                    </button>
                                                )}

                                            </td>

                                        </tr>

                                    ))

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>
                        {/* ==========================================================
                INVOICE DETAILS MODAL
            ========================================================== */}

            <Modal
                show={showDetail}
                onHide={() => setShowDetail(false)}
                size="lg"
                centered
            >

                <Modal.Header closeButton>
                    <Modal.Title>
                        Receivable Invoice Details
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>

                    {detailLoading ? (

                        <div className="text-center py-4">
                            <Spinner />
                            Loading invoice details...
                        </div>

                    ) : (

                        <>

                            <div className="row g-3 mb-3">

                                <div className="col-md-6">
                                    <div className="text-muted small">
                                        Invoice No.
                                    </div>
                                    <strong>
                                        {selected?.invoice_no || "—"}
                                    </strong>
                                </div>

                                <div className="col-md-6">
                                    <div className="text-muted small">
                                        Customer
                                    </div>
                                    <strong>
                                        {selected?.customer_name || "—"}
                                    </strong>
                                </div>

                                <div className="col-md-4">
                                    <div className="text-muted small">
                                        Invoice Amount
                                    </div>
                                    <strong>
                                        {amount(
                                            pick(
                                                outstandingInfo,
                                                "invoice_amount"
                                            ) ?? selected?.invoice_amount,
                                            selected?.currency
                                        )}
                                    </strong>
                                </div>

                                <div className="col-md-4">
                                    <div className="text-muted small">
                                        Received Amount
                                    </div>
                                    <strong className="text-success">
                                        {amount(
                                            pick(
                                                outstandingInfo,
                                                "received_amount"
                                            ) ?? selected?.received_amount,
                                            selected?.currency
                                        )}
                                    </strong>
                                </div>

                                <div className="col-md-4">
                                    <div className="text-muted small">
                                        Outstanding
                                    </div>
                                    <strong className="text-danger">
                                        {amount(
                                            pick(
                                                outstandingInfo,
                                                "outstanding_amount"
                                            ) ?? selected?.outstanding_amount,
                                            selected?.currency
                                        )}
                                    </strong>
                                </div>

                            </div>

                            <div className="alert alert-light border small mb-0">
                                Only posted receipt vouchers reduce the invoice outstanding.
                                Draft receipts remain pending in the existing Receipt Voucher workflow.
                            </div>

                        </>

                    )}

                </Modal.Body>

                <Modal.Footer>

                    <button
                        className="btn btn-light border"
                        onClick={() => setShowDetail(false)}
                    >
                        Close
                    </button>

                    {selected &&
                        selected.payment_status !== "PAID" &&
                        Number(selected.outstanding_amount || 0) > 0 && (
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setShowDetail(false);
                                    openReceipt(selected);
                                }}
                            >
                                Receive Payment
                            </button>
                        )}

                </Modal.Footer>

            </Modal>

            {/* ==========================================================
                RECEIVE PAYMENT MODAL
            ========================================================== */}

            <Modal
                show={showReceipt}
                onHide={() =>
                    !saving && setShowReceipt(false)
                }
                centered
            >

                <form onSubmit={submitReceipt}>

                    <Modal.Header closeButton={!saving}>
                        <Modal.Title>
                            Receive Payment
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>

                        <div className="mb-3">

                            <div className="small text-muted">
                                Invoice
                            </div>

                            <strong>
                                {selected?.invoice_no || "—"}
                            </strong>

                            <div className="small text-muted">
                                {selected?.customer_name || ""}
                            </div>

                            <div className="small mt-1">
                                Outstanding:{" "}
                                <strong>
                                    {amount(
                                        selected?.outstanding_amount,
                                        selected?.currency
                                    )}
                                </strong>
                            </div>

                        </div>

                        <div className="row g-3">

                            <div className="col-md-6">

                                <label className="form-label">
                                    Receipt Date *
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    value={receiptDate}
                                    onChange={(e) =>
                                        setReceiptDate(e.target.value)
                                    }
                                    required
                                />

                            </div>

                            <div className="col-md-6">

                                <label className="form-label">
                                    Receipt Amount *
                                </label>

                                <input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    max={selected?.outstanding_amount}
                                    className="form-control"
                                    value={receiptAmount}
                                    onChange={(e) =>
                                        setReceiptAmount(e.target.value)
                                    }
                                    required
                                />

                            </div>

                            <div className="col-md-6">

                                <label className="form-label">
                                    Receipt Mode *
                                </label>

                                <select
                                    className="form-select"
                                    value={receiptMode}
                                    onChange={(e) =>
                                        setReceiptMode(e.target.value)
                                    }
                                    required
                                >
                                    <option value="BANK">Bank</option>
                                    <option value="CASH">Cash</option>
                                    <option value="CHEQUE">Cheque</option>
                                    <option value="UPI">UPI</option>
                                    <option value="NEFT">NEFT / RTGS</option>
                                </select>

                            </div>

                            <div className="col-md-6">

                                <label className="form-label">
                                    Receipt Account *
                                </label>

                                <AsyncSelect
                                    cacheOptions
                                    defaultOptions
                                    loadOptions={loadPaymentAccounts}
                                    value={receiptAccount}
                                    onChange={setReceiptAccount}
                                    placeholder="Search bank / cash account..."
                                    isClearable
                                />

                            </div>

                            <div className="col-md-6">

                                <label className="form-label">
                                    Reference No.
                                </label>

                                <input
                                    className="form-control"
                                    value={referenceNo}
                                    onChange={(e) =>
                                        setReferenceNo(e.target.value)
                                    }
                                />

                            </div>

                            <div className="col-md-6">

                                <label className="form-label">
                                    Reference Date
                                </label>

                                <input
                                    type="date"
                                    className="form-control"
                                    value={referenceDate}
                                    onChange={(e) =>
                                        setReferenceDate(e.target.value)
                                    }
                                />

                            </div>

                            <div className="col-12">

                                <label className="form-label">
                                    Remarks
                                </label>

                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={remarks}
                                    onChange={(e) =>
                                        setRemarks(e.target.value)
                                    }
                                />

                            </div>

                        </div>

                        {formError && (
                            <div className="alert alert-danger py-2 mt-3 mb-0">
                                {formError}
                            </div>
                        )}

                        <div className="alert alert-info py-2 mt-3 mb-0 small">
                            This creates a DRAFT Finance Receipt Voucher.
                            Approval and posting will continue through the
                            existing Finance Receipt Voucher workflow.
                        </div>

                    </Modal.Body>

                    <Modal.Footer>

                        <button
                            type="button"
                            className="btn btn-light border"
                            onClick={() => setShowReceipt(false)}
                            disabled={saving}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Spinner
                                        size="sm"
                                        className="me-2"
                                    />
                                    Saving...
                                </>
                            ) : (
                                "Create Draft Receipt"
                            )}
                        </button>

                    </Modal.Footer>

                </form>

            </Modal>

        </div>
    );
}